import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import {
  FILESTACK_CDN_WIDTHS,
  ensureFilestackCdnResized,
  filestackUrlFromPhotoHandles
} from "../../../utils/filestackCdn"
import { formatDurationWithSessions, getServicePriceRange } from "../../../utils/utils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { isHomeBuilderPageType, isPackagesBuilderPageType } from "../utils/pageUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import {
  buildPublicPageUrl,
  buildServiceUrl,
  buildBookingUrl,
  buildDefaultPageUrl,
  resolveBuilderPageSlug
} from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"
import { CARD_IMAGE_HOVER_CLASSES, CARD_IMAGE_OVERLAY_CLASSES } from "./shared/scrollRevealVariants"

const PackagesSectionMobile = ({ section, practice, theme, highlightedField, pageSlug, defaultPageType = null }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || "Browse Packages"
  const ctaAction = settings.ctaAction || "page"
  const packagesPageSlug = settings.ctaTarget || resolveBuilderPageSlug(practice, null, "packages") || "packages"
  const ctaTarget = packagesPageSlug
  const selectedPackageIds = settings.selectedPackageIds || []

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)

  // Use color scheme for colors
  const sectionBackgroundColor = colorScheme.sectionBackground

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const handlePackageClick = (pkg) => {
    window.location.href = buildServiceUrl(pkg, practice)
  }

  // Get practice packages
  const allPracticePackages = practice?.packages || []

  // If specific packages are selected, use those; otherwise use all packages
  const selectedPackages =
    selectedPackageIds.length > 0
      ? allPracticePackages.filter((pkg) => selectedPackageIds.includes(pkg.id))
      : allPracticePackages

  // Combine packages, limit based on layout and page (by defaultPageType or slug fallback)
  const isHomePage = isHomeBuilderPageType(defaultPageType, pageSlug, practice)
  const isOnPackagesPage = isPackagesBuilderPageType(pageSlug, practice, packagesPageSlug)
  const maxItems = isHomePage
    ? layout === "highlights_2"
      ? 2
      : layout === "highlights_3"
      ? 3
      : 3 // Limit to 3 for list layout when on home page
    : 999 // Show all items on non-home pages
  const allItems = selectedPackages.slice(0, maxItems)
  const hasMorePackages = selectedPackages.length > maxItems

  // Render package card for mobile (vertical stack with image on top)
  const renderMobilePackageCard = (item, index) => {
    const price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
    const timeLength = typeof item.timeLength === "number" ? item.timeLength : item.timeLength?.value || item.timeLength
    const duration = timeLength ? formatDurationWithSessions(timeLength, item) : ""

    // Format location and service type information
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl = ensureFilestackCdnResized(
      item.imageUrl ||
        item.mediumWebpUrl ||
        filestackUrlFromPhotoHandles(item.photoHandles, { width: FILESTACK_CDN_WIDTHS.card }),
      { width: FILESTACK_CDN_WIDTHS.card }
    )

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <ScrollRevealItem
        key={index}
        staggerIndex={index + 1}
        className="group flex w-full flex-col items-start justify-start gap-4">
        <div className="relative aspect-[396/250] w-full overflow-hidden bg-gray-100">
          <img
            src={imageUrl || placeholderImage}
            alt={item.name}
            className={twMerge("size-full object-cover", CARD_IMAGE_HOVER_CLASSES)}
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
          <div className={CARD_IMAGE_OVERLAY_CLASSES} style={{ backgroundColor: "rgba(0,0,0,0.06)" }} />
        </div>

        <div className="flex w-full flex-col items-start justify-start gap-2">
          {/* Price and Title */}
          <div className="flex w-full flex-col items-start justify-start">
            {price && (
              <div
                className="text-[18px] font-semibold leading-[24px]"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                {price}
              </div>
            )}
            <div
              className="line-clamp-2 text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#000000",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}>
              {item.name}
            </div>
          </div>

          {/* Package Badge */}
          <span
            className="rounded-full text-xs font-semibold uppercase tracking-wider"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              color: "#FFFFFF",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              padding: "0.25rem 0.75rem"
            }}>
            {item.numberOfSessions ? `${item.numberOfSessions} Session Package` : "Package"}
          </span>

          {/* Duration and Location */}
          {(duration || locationInfo) && (
            <div
              className="text-[14px] font-normal leading-[20px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </div>
          )}

          {/* Description */}
          {displayDescription && (
            <div
              className="prose prose-lg max-w-none text-[16px] font-normal leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#000000"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
          )}

          {/* Details button */}
          <div
            className="flex cursor-pointer items-center justify-start gap-3 px-10 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              borderRadius: theme?.borderRadius || "12px"
            }}
            onClick={() => handlePackageClick(item)}>
            <div
              className="text-[16px] font-normal leading-[24px] text-white"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9"
              }}>
              Details
            </div>
          </div>
        </div>
      </ScrollRevealItem>
    )
  }

  return (
    <section
      className={twMerge("relative py-8 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <div className="mx-auto max-w-[1160px] px-5">
        <div className="flex w-full flex-col items-start gap-8">
          <ScrollRevealItem staggerIndex={0} className="flex w-full flex-col items-start gap-8">
            <h2
              className="w-full text-left text-[28px] font-bold leading-[32px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {section.headline || "Packages"}
            </h2>
            {section.body && (
              <p
                className="max-w-3xl text-left text-[18px] leading-[24px]"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                {section.body}
              </p>
            )}
          </ScrollRevealItem>

          {/* Packages Grid */}
          {allItems.length > 0 && (
            <div className="w-full">
              {layout === "highlights_2" || layout === "highlights_3" ? (
                // Highlights layout - grid for mobile
                <div
                  className={`grid gap-6 ${layout === "highlights_2" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                  {allItems.map((item, index) => renderMobilePackageCard(item, index))}
                </div>
              ) : (
                // List layout - vertical stack
                <div className="flex flex-col gap-10">
                  {allItems.map((item, index) => renderMobilePackageCard(item, index))}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allItems.length === 0 && (
            <div className="flex flex-col items-center gap-6 py-16 text-center">
              <div className="text-6xl">📦</div>
              <h3
                className="text-2xl font-semibold"
                style={{
                  fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                No packages available
              </h3>
              <p
                className="text-lg text-gray-600"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                {practice?.packages?.length === 0
                  ? "This practice hasn't added any packages yet."
                  : "No packages match the current selection."}
              </p>
            </div>
          )}

          {/* Main CTA Button - Only show if not on packages page */}
          {showCTA && allItems.length > 0 && !isOnPackagesPage && (
            <div className="flex justify-start pt-4">
              {ctaAction === "booking" ? (
                <button
                  onClick={() => (window.location.href = buildDefaultPageUrl(practice, null, packagesPageSlug))}
                  className={twMerge(
                    "inline-flex items-center justify-center px-10 py-3 text-[16px] font-semibold text-white transition-all duration-300 hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    e.target.style.transform = "translateY(0)"
                  }}>
                  {ctaText}
                </button>
              ) : (
                <button
                  onClick={handleCTAClick}
                  className={twMerge(
                    "inline-flex items-center justify-center px-10 py-3 text-[16px] font-semibold text-white transition-all duration-300 hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    e.target.style.transform = "translateY(0)"
                  }}>
                  {ctaText}
                </button>
              )}
            </div>
          )}

          {/* Show More Button for non-home pages */}
          {hasMorePackages && !isHomePage && (
            <div className="mt-8 text-left">
              <button
                onClick={() => (window.location.href = buildDefaultPageUrl(practice, "services", "services"))}
                className="rounded-lg px-8 py-3 font-medium text-white transition-colors"
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.primary || "#BD654F",
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    borderRadius: theme?.borderRadius || "12px"
                  },
                  theme
                )}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  e.target.style.transform = "translateY(-2px)"
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  e.target.style.transform = "translateY(0)"
                }}>
                View All Packages
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default PackagesSectionMobile
